import ReactGA from "react-ga4";

import type { AnalyticsEvent, PageViewEvent } from "../types";
import type { Destination } from "./types";

/**
 * Google Analytics 4. Previously the only analytics sink, wired directly into
 * `router.subscribe` in index.tsx; now one destination among several so that
 * both it and our own backend agree on what a page view is.
 *
 * Behaviour is preserved exactly, including the measurement-id lookup below.
 */

// `GA_MEASUREMENT_ID` has no VITE_ prefix on purpose, and that is not a typo —
// devops/generate-env-js.sh emits exactly that key into the deployed window.env
// and documents the choice. Renaming it here silently turns GA4 off in staging and
// production, because the bundle is built with no VITE_* values baked in, so the
// import.meta.env fallback is empty there.
//
// The build-time fallback is what serves local dev and tests.
const measurementId = (): string | undefined =>
  (window as any)?.env?.GA_MEASUREMENT_ID || import.meta.env.VITE_GA_MEASUREMENT_ID;

export const ga4Destination: Destination = {
  name: "ga4",

  init() {
    const id = measurementId();
    if (id) ReactGA.initialize(id);
  },

  isEnabled() {
    return Boolean(measurementId());
  },

  // GA4 only ever received page views from this app (plus one manual event in
  // CreateProject, which still calls ReactGA directly). Keeping the filter
  // explicit means a new event type does not silently start reporting here.
  accepts(event: AnalyticsEvent): event is PageViewEvent {
    return event.name === "page_view";
  },

  send(event: AnalyticsEvent) {
    // Repeated rather than relying on `accepts` above, because a type predicate
    // does not narrow across method boundaries — `send` receives the full union and
    // `event.properties` only has a `path` on one arm of it. Cheap, and it keeps
    // this destination correct even if `track()` ever stopped consulting `accepts`.
    if (event.name !== "page_view") return;

    try {
      // GA's own page dimension wants path + search, which is what it recorded
      // before. Our allowlisting applies to *our* store, not to what GA already
      // collects — this is the same string it has always received.
      ReactGA.set({ page: event.properties.path });
      ReactGA.send("pageview");
    } catch {
      // A blocked or failed GA script must not stop the RebelBase destination.
    }
  },
};
